import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import GameIcon from "components/admin/GameIcon";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatNumber } from "utils/format";
import {
  itemTypeLabel,
  elementLabel,
  nclassLabel,
  rangeText,
  optionSummary,
} from "layouts/templates/labels";
import {
  ItemEditDialog,
  PetEditDialog,
  AchievementEditDialog,
  TattooEditDialog,
} from "layouts/templates/EditDialogs";

const DAY_MS = 86400000;

/** Ô tên kèm icon như trong game, mô tả nhỏ bên dưới. */
function NameCell({ icon, fallback, name, description }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1.5}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={44}
        height={44}
        flexShrink={0}
        borderRadius="md"
        sx={{ bgcolor: "action.hover" }}
      >
        <GameIcon path={icon} size={36} fallback={fallback} />
      </MDBox>
      <MDBox minWidth={0}>
        <MDTypography variant="button" fontWeight="medium" display="block">
          {name}
        </MDTypography>
        {description && (
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ whiteSpace: "normal", maxWidth: 420 }}
          >
            {description}
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  );
}

NameCell.defaultProps = { icon: null, fallback: "category", description: null };
NameCell.propTypes = {
  icon: PropTypes.string,
  fallback: PropTypes.string,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
};

function EditButton({ onClick }) {
  return (
    <MDButton size="small" variant="outlined" color="warning" onClick={onClick}>
      <Icon>edit</Icon>&nbsp;Sửa
    </MDButton>
  );
}

EditButton.propTypes = { onClick: PropTypes.func.isRequired };

function editColumn(canEdit, onEdit) {
  if (!canEdit) return null;
  return {
    key: "actions",
    label: "",
    align: "right",
    render: (row) => <EditButton onClick={() => onEdit(row)} />,
  };
}

/** Bảng tìm kiếm phân trang cho vật phẩm / pet (API trả PagedResult). */
function PagedLookup({ path, columns, rowKey, filters, refreshKey }) {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const { data, loading, error } = useFetch(
    () => api.get(path, { q, type, page, size }),
    [path, q, type, page, size, refreshKey]
  );

  return (
    <>
      <MDBox
        px={2}
        pb={1}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setQ(input.trim());
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <MDInput
              fullWidth
              label="Tên hoặc ID"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </Grid>
          {filters && (
            <Grid item xs={8} md={3}>
              <MDInput
                select
                fullWidth
                label="Loại"
                value={type}
                onChange={(e) => {
                  setPage(1);
                  setType(e.target.value);
                }}
                SelectProps={{ sx: { height: 44 } }}
              >
                <MenuItem value="">Tất cả</MenuItem>
                {filters.map((f) => (
                  <MenuItem key={f.type} value={f.type}>
                    {itemTypeLabel(f.type)} ({f.count})
                  </MenuItem>
                ))}
              </MDInput>
            </Grid>
          )}
          <Grid item xs="auto">
            <MDButton type="submit" variant="gradient" color="info">
              <Icon>search</Icon>&nbsp;Tìm
            </MDButton>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        rowKey={rowKey}
        page={page}
        size={size}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onSizeChange={(s) => {
          setSize(s);
          setPage(1);
        }}
        columns={columns}
      />
    </>
  );
}

PagedLookup.defaultProps = { filters: null, refreshKey: 0 };
PagedLookup.propTypes = {
  path: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowKey: PropTypes.string.isRequired,
  filters: PropTypes.arrayOf(PropTypes.object),
  refreshKey: PropTypes.number,
};

function ClientFilterTable({ rows, loading, columns, rowKey, match }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    return text ? rows.filter((row) => match(row, text)) : rows;
  }, [rows, q]);
  return (
    <>
      <MDBox px={2} pb={1} maxWidth={400}>
        <MDInput
          fullWidth
          label="Lọc theo tên hoặc ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </MDBox>
      <AdminTable loading={loading} rows={filtered} rowKey={rowKey} columns={columns} />
    </>
  );
}

ClientFilterTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowKey: PropTypes.string.isRequired,
  match: PropTypes.func.isRequired,
};

const byNameOrId = (idKey) => (row, text) =>
  String(row[idKey]) === text || (row.name || "").toLowerCase().includes(text);

function itemStats(i) {
  const parts = [
    ["Công", i.atkRange],
    ["Thủ", i.defRange],
    ["HP", i.hpRange],
    ["MP", i.mpRange],
  ]
    .filter(([, range]) => range && range.length)
    .map(([label, range]) => `${label} ${rangeText(range)}`);
  const need = [
    ["Sức", i.requireStr],
    ["Nhanh", i.requireAgi],
    ["Trí", i.requireInt],
  ]
    .filter(([, v]) => v > 0)
    .map(([label, v]) => `${label} ${v}`);
  const options = i.isEquip ? [] : optionSummary(i);
  return (
    <MDBox>
      {parts.length > 0 && (
        <MDTypography variant="caption" display="block">
          {parts.join(" · ")}
        </MDTypography>
      )}
      {options.map((line) => (
        <MDTypography variant="caption" color="info" display="block" key={line}>
          {line}
        </MDTypography>
      ))}
      {need.length > 0 && (
        <MDTypography variant="caption" color="text" display="block">
          Yêu cầu: {need.join(", ")}
        </MDTypography>
      )}
      {!parts.length && !need.length && !options.length && "-"}
    </MDBox>
  );
}

function ItemsTab({ itemTypes }) {
  const [editing, setEditing] = useState(null);
  const canEdit = useCan(PERMISSIONS.TEMPLATE_EDIT);
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <>
      <PagedLookup
        path="/api/admin/templates/items"
        rowKey="itemId"
        filters={itemTypes}
        refreshKey={refreshKey}
        columns={[
          { key: "itemId", label: "ID", width: 70 },
          {
            key: "name",
            label: "Vật phẩm",
            wrap: true,
            render: (i) => (
              <NameCell
                icon={i.iconPath}
                fallback="inventory_2"
                name={i.name}
                description={i.description}
              />
            ),
          },
          {
            key: "type",
            label: "Loại",
            render: (i) => <Chip size="small" label={itemTypeLabel(i.type)} />,
          },
          { key: "stats", label: "Chỉ số", wrap: true, render: itemStats },
          {
            key: "flags",
            label: "Thuộc tính",
            render: (i) => (
              <MDBox display="flex" gap={0.5} flexWrap="wrap">
                {i.isStackable && <Chip size="small" variant="outlined" label="Cộng dồn" />}
                <Chip
                  size="small"
                  variant="outlined"
                  color={i.canTrade ? "success" : "default"}
                  label={i.canTrade ? "Giao dịch được" : "Khoá giao dịch"}
                />
                {i.expire > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`HSD ${Math.round(i.expire / DAY_MS)} ngày`}
                  />
                )}
              </MDBox>
            ),
          },
          {
            key: "price",
            label: "Giá sàn",
            align: "right",
            render: (i) => formatNumber(i.price),
          },
          editColumn(canEdit, setEditing),
        ].filter(Boolean)}
      />
      {editing && (
        <ItemEditDialog
          item={editing}
          onClose={() => setEditing(null)}
          onDone={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </>
  );
}

ItemsTab.propTypes = { itemTypes: PropTypes.arrayOf(PropTypes.object).isRequired };

function PetsTab() {
  const [editing, setEditing] = useState(null);
  const canEdit = useCan(PERMISSIONS.TEMPLATE_EDIT);
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <>
      <PagedLookup
        path="/api/admin/templates/pets"
        rowKey="petId"
        refreshKey={refreshKey}
        columns={[
          { key: "petId", label: "ID", width: 70 },
          {
            key: "name",
            label: "Pet",
            render: (p) => <NameCell icon={p.icon} fallback="pets" name={p.name} />,
          },
          { key: "element", label: "Hệ", render: (p) => elementLabel(p.element) },
          { key: "nclass", label: "Phái", render: (p) => nclassLabel(p.nclass) },
          {
            key: "stats",
            label: "Sức / Nhanh / Trí",
            render: (p) => `${p.str} / ${p.agi} / ${p._int}`,
          },
          {
            key: "hpmp",
            label: "HP / MP gốc",
            render: (p) => `${formatNumber(p.hp)} / ${formatNumber(p.mp)}`,
          },
          editColumn(canEdit, setEditing),
        ].filter(Boolean)}
      />
      {editing && (
        <PetEditDialog
          pet={editing}
          onClose={() => setEditing(null)}
          onDone={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </>
  );
}

function titleStats(a) {
  const parts = [
    ["Công", a.atk],
    ["Thủ", a.def],
    ["HP", a.hp],
    ["MP", a.mp],
    ["Sức", a.str],
    ["Nhanh", a.agi],
    ["Trí", a._int],
  ]
    .filter(([, v]) => v > 0)
    .map(([label, v]) => `+${formatNumber(v)} ${label}`);
  return parts.join(", ") || "-";
}

function AchievementsTab() {
  const { data, loading, reload } = useFetch(
    () => api.get("/api/admin/templates/achievements"),
    []
  );
  const [editing, setEditing] = useState(null);
  const canEdit = useCan(PERMISSIONS.TEMPLATE_EDIT);
  return (
    <>
      <ClientFilterTable
        rows={data || []}
        loading={loading}
        rowKey="id"
        match={byNameOrId("id")}
        columns={[
          { key: "id", label: "ID", width: 70 },
          {
            key: "name",
            label: "Danh hiệu",
            wrap: true,
            render: (a) => (
              <NameCell
                icon={a.iconPath}
                fallback="military_tech"
                name={a.name}
                description={a.description}
              />
            ),
          },
          { key: "stats", label: "Chỉ số cộng thêm", wrap: true, render: titleStats },
          editColumn(canEdit, setEditing),
        ].filter(Boolean)}
      />
      {editing && (
        <AchievementEditDialog
          title={editing}
          onClose={() => setEditing(null)}
          onDone={() => reload(true)}
        />
      )}
    </>
  );
}

function tattooStats(t) {
  return (
    [
      ["Công", t.atk],
      ["Thủ", t.def],
      ["HP", t.hp],
      ["MP", t.mp],
    ]
      .filter(([, v]) => v > 0)
      .map(([label, v]) => `${label} ${formatNumber(v)}`)
      .join(" · ") || "-"
  );
}

function TattoosTab() {
  const { data, loading, reload } = useFetch(() => api.get("/api/admin/templates/tattoos"), []);
  const [editing, setEditing] = useState(null);
  const canEdit = useCan(PERMISSIONS.TEMPLATE_EDIT);
  return (
    <>
      <ClientFilterTable
        rows={data || []}
        loading={loading}
        rowKey="id"
        match={byNameOrId("id")}
        columns={[
          { key: "id", label: "ID", width: 70 },
          {
            key: "name",
            label: "Hình xăm",
            render: (t) => <NameCell icon={t.iconPath} fallback="brush" name={t.name} />,
          },
          { key: "stats", label: "Chỉ số gốc (+0)", wrap: true, render: tattooStats },
          {
            key: "max",
            label: "+10 (gấp đôi)",
            wrap: true,
            render: (t) =>
              tattooStats({ atk: t.atk * 2, def: t.def * 2, hp: t.hp * 2, mp: t.mp * 2 }),
          },
          {
            key: "percent",
            label: "Độ dễ ra",
            align: "right",
            render: (t) => `${t.percent}%`,
          },
          editColumn(canEdit, setEditing),
        ].filter(Boolean)}
      />
      {editing && (
        <TattooEditDialog
          tattoo={editing}
          onClose={() => setEditing(null)}
          onDone={() => reload(true)}
        />
      )}
    </>
  );
}

function MapsTab() {
  const { data, loading } = useFetch(() => api.get("/api/admin/templates/maps"), []);
  return (
    <ClientFilterTable
      rows={data || []}
      loading={loading}
      rowKey="mapId"
      match={byNameOrId("mapId")}
      columns={[
        { key: "mapId", label: "ID", width: 80 },
        { key: "name", label: "Tên" },
        { key: "zones", label: "Số khu", align: "right", render: (m) => m.zones.length },
        {
          key: "bosses",
          label: "Boss",
          wrap: true,
          render: (m) => m.bosses.map((b) => b.name).join(", ") || "-",
        },
      ]}
    />
  );
}

function Templates() {
  // Tab ghi lên URL (?tab=0..4) để gửi link thẳng.
  const [params, setParams] = useSearchParams();
  const tab = [0, 1, 2, 3, 4].includes(Number(params.get("tab"))) ? Number(params.get("tab")) : 0;
  const setTab = (v) => setParams(v === 0 ? {} : { tab: String(v) }, { replace: true });
  const itemTypes = useFetch(() => api.get("/api/admin/templates/item-types"), []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="templates" />
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Vật phẩm" />
            <Tab label="Pet" />
            <Tab label="Danh hiệu" />
            <Tab label="Hình xăm" />
            <Tab label="Map" />
          </Tabs>
        </Card>
        {tab === 0 && (
          <PageCard
            title="Vật phẩm"
            subtitle="Trang bị, ngọc, cánh, cải trang và mọi vật phẩm khác. Lọc theo loại rồi bấm Sửa; sửa xong áp dụng ngay"
            noPadding
          >
            <ItemsTab itemTypes={itemTypes.data || []} />
          </PageCard>
        )}
        {tab === 1 && (
          <PageCard title="Pet" color="success" noPadding>
            <PetsTab />
          </PageCard>
        )}
        {tab === 2 && (
          <PageCard title="Danh hiệu" color="warning" noPadding>
            <AchievementsTab />
          </PageCard>
        )}
        {tab === 3 && (
          <PageCard title="Hình xăm" color="error" noPadding>
            <TattoosTab />
          </PageCard>
        )}
        {tab === 4 && (
          <PageCard title="Map" color="dark" noPadding>
            <MapsTab />
          </PageCard>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Templates;
